// Peta definisi halaman Price Protection (brief owner 22 Sep 2026, A1–A5) — SATU
// sumber untuk empat halaman (Support Aktivasi / Support Cashback × ZOA GROUP /
// Dealer Partner), kolom tabel unit per halaman, kolom tabel SKU & item batch
// per jenis, dan pembangun body form program.
//
// KENAPA SATU BERKAS TANPA JSX & TANPA IMPOR: tes node memuatnya apa adanya,
// membandingkan nilainya dengan konstanta backend (`routers/pp/aturan.py`), dan
// menjalankan setiap fungsi `isi` kolom pada baris respons backend sungguhan.
// Kalau kolom diketik keras di tiap tabel, satu halaman lupa diubah dan "Modal"
// kembali tampil di halaman yang brief-nya berkata DIHAPUS.

export const JENIS = Object.freeze({ AKTIVASI: "aktivasi", CASHBACK: "cashback" });
export const KANAL = Object.freeze({ ZOA: "zoa_group", DEALER: "dealer_partner" });
export const MODE = Object.freeze({ KEANGGOTAAN: "keanggotaan", LAMA: "lama" });
export const LABEL_JENIS = Object.freeze({ aktivasi: "Support Aktivasi", cashback: "Support Cashback" });
export const LABEL_KANAL = Object.freeze({ zoa_group: "ZOA GROUP", dealer_partner: "Dealer Partner" });
// Bagian tabel unit (brief hal. 7 + pertanyaan no. 20): HP yang masih di gudang
// tampil di bagian "Belum Terjual" halaman Cashback ZOA GROUP.
export const BAGIAN = Object.freeze({ TERJUAL: "terjual", BELUM_TERJUAL: "belum_terjual" });
export const LABEL_BAGIAN = Object.freeze({ terjual: "Terjual", belum_terjual: "Belum Terjual (stok gudang)" });

export const PATH_PROGRAM = "/finance/cashback/programs";
export const HALAMAN_SEMUA = "semua";

// Empat halaman brief (hal. 1–2). `slug*` = bagian URL menu 3 tingkat
// (`/finance/cashback/programs/:jenis/:kanal`); `kunci` = nilai `?halaman=`.
export const HALAMAN_PP = Object.freeze([
  { kunci: "aktivasi-zoa", jenis: "aktivasi", kanal: "zoa_group", slugJenis: "aktivasi", slugKanal: "zoa-group",
    label: "Support Aktivasi ZOA GROUP" },
  { kunci: "aktivasi-dealer", jenis: "aktivasi", kanal: "dealer_partner", slugJenis: "aktivasi",
    slugKanal: "dealer-partner", label: "Support Aktivasi Dealer Partner" },
  { kunci: "cashback-zoa", jenis: "cashback", kanal: "zoa_group", slugJenis: "cashback", slugKanal: "zoa-group",
    label: "Support Cashback ZOA GROUP" },
  { kunci: "cashback-dealer", jenis: "cashback", kanal: "dealer_partner", slugJenis: "cashback",
    slugKanal: "dealer-partner", label: "Support Cashback Dealer Partner" },
]);

export function halamanDariKunci(kunci) {
  return HALAMAN_PP.find((h) => h.kunci === kunci) || null;
}

export function halamanDari(jenis, kanal) {
  return HALAMAN_PP.find((h) => h.jenis === jenis && h.kanal === kanal) || null;
}

export function halamanDariSlug(slugJenis, slugKanal) {
  return HALAMAN_PP.find((h) => h.slugJenis === slugJenis && h.slugKanal === slugKanal) || null;
}

export function pathHalaman(h) {
  return `${PATH_PROGRAM}/${h.slugJenis}/${h.slugKanal}`;
}

// Istilah nominal hak per jenis (label kartu dashboard, kolom Total, dst).
export function istilahNominal(jenis) {
  return jenis === JENIS.AKTIVASI ? "Support Aktivasi" : "Cashback";
}

// ============ Tampilan program — dari keputusan BACKEND ============
// `_hias_program` mengirim `jenis_efektif` / `kanal_efektif` / `mode_efektif`
// (pembaca tunggal `routers/pp/aturan.py`). Layar TIDAK menafsirkan
// `mode_mesin`/`jenis_program` mentah — dokumen tanpa medan efektif dibaca
// sebagai program lama (tampilan paling konservatif).
export function modeProgram(p) {
  return p && p.mode_efektif === MODE.KEANGGOTAAN ? MODE.KEANGGOTAAN : MODE.LAMA;
}

export function jenisProgram(p) {
  return (p && p.jenis_efektif) || null;
}

// Kunci tabel unit program: halaman jenis × kanal, atau "lama".
export function kunciTampilan(p) {
  if (modeProgram(p) !== MODE.KEANGGOTAAN) return MODE.LAMA;
  const h = halamanDari(p.jenis_efektif, p.kanal_efektif);
  return h ? h.kunci : MODE.LAMA;
}

// Kunci tabel SKU / item batch: jenis (program jenis baru) atau "lama".
export function kunciJenis(tampilan) {
  if (!tampilan || tampilan.mode !== MODE.KEANGGOTAAN) return MODE.LAMA;
  return Object.values(JENIS).includes(tampilan.jenis) ? tampilan.jenis : MODE.LAMA;
}

export function labelProgram(p) {
  if (modeProgram(p) === MODE.KEANGGOTAAN) {
    return `${LABEL_JENIS[p.jenis_efektif] || "-"} · ${LABEL_KANAL[p.kanal_efektif] || "-"}`;
  }
  return p && p.jenis_efektif
    ? `Program lama · ditandai ${LABEL_JENIS[p.jenis_efektif]}`
    : "Program lama · jenis belum ditetapkan";
}

// ============ Kolom tabel UNIT per halaman (brief hal. 3–4 & 7–8) ============
// `format` dibaca komponen tabel: imei | mono | teks | produk | do_masuk |
// tanggal | uang | nominal | status | alasan | kosong. `tid` = akhiran
// data-testid tombol urut (yang lama dipertahankan).
const U_IMEI = { kunci: "imei", label: "IMEI", format: "imei", tid: "imei", isi: (k) => k.imei, urut: (k) => k.imei || "" };
const U_SN = { kunci: "sn", label: "SN", format: "mono", tid: "sn", isi: (k) => k.serial_number, urut: (k) => k.serial_number || "" };
const U_PRODUK = { kunci: "produk", label: "Produk", format: "produk", tid: "produk",
  isi: (k) => ({ nama: k.nama_produk, sku: k.sku }), urut: (k) => k.nama_produk || "" };
const U_UNIT_PRODUK = { ...U_PRODUK, label: "Unit/Produk" };
const U_SUPPLIER = { kunci: "supplier", label: "Supplier", format: "teks", tid: "supplier",
  isi: (k) => k.supplier_nama, urut: (k) => k.supplier_nama || "" };
// Brief: "DO / Invoice = Tanggal Masuk" — nomor DO/Invoice penerimaan + tanggal unit masuk.
const U_DO_MASUK = { kunci: "tgl_masuk", label: "DO/Invoice · Tgl Masuk", format: "do_masuk", tid: "tglmasuk",
  isi: (k) => ({ nomor: k.no_do_supplier || k.po_code || null, tanggal: k.tanggal_masuk }),
  urut: (k) => k.tanggal_masuk || "" };
const U_TGL_TERJUAL = { kunci: "tgl_terjual", label: "Tgl Terjual", format: "tanggal", tid: "tglterjual",
  isi: (k) => k.tanggal_terjual, urut: (k) => k.tanggal_terjual || "" };
const U_PELANGGAN_B2B = { kunci: "pelanggan", label: "Nama Pelanggan (B2B)", format: "teks", tid: "pelanggan",
  isi: (k) => k.nama_pelanggan, urut: (k) => k.nama_pelanggan || "" };
const U_CABANG = { kunci: "cabang", label: "Nama Cabang", format: "teks", tid: "cabang",
  isi: (k) => k.cabang_nota, urut: (k) => k.cabang_nota || "" };
const U_TGL_AKTIVASI = { kunci: "tgl_aktivasi", label: "Tgl Aktivasi", format: "tanggal", tid: "tglaktivasi",
  isi: (k) => k.tanggal_aktivasi, urut: (k) => k.tanggal_aktivasi || "" };
const U_NOMINAL_SUPPORT = { kunci: "nominal", label: "Nominal Support Aktivasi", format: "nominal", tid: "cashback",
  isi: (k) => k.cashback_nominal, urut: (k) => k.cashback_nominal || 0 };
// "Modal Asli Diganti Menjadi STP PROMO yang terhubung dengan Harga Lama" (hal. 7).
const U_STP = { kunci: "stp", label: "STP PROMO", format: "uang", tid: "stp",
  isi: (k) => k.harga_lama, urut: (k) => k.harga_lama || 0 };
const U_HARGA_BARU = { kunci: "harga_baru", label: "Harga Baru", format: "uang", tid: "hargabaru",
  isi: (k) => k.harga_baru, urut: (k) => k.harga_baru || 0 };
const U_NOMINAL_CB = { kunci: "nominal", label: "Nominal Cashback", format: "nominal", tid: "cashback",
  isi: (k) => k.cashback_nominal, urut: (k) => k.cashback_nominal || 0 };
const U_STATUS = { kunci: "status", label: "Status", format: "status", tid: "status",
  isi: (k) => k.status, urut: (k) => k.status || "" };
const U_ALASAN = { kunci: "alasan", label: "Alasan", format: "alasan", isi: (k) => k.alasan };
// Program LAMA: kolom hari ini apa adanya (rumus modal − harga baru).
const U_TGL_BELI = { kunci: "tgl_masuk", label: "Tgl Beli", format: "tanggal", tid: "tglmasuk",
  isi: (k) => k.tanggal_masuk, urut: (k) => k.tanggal_masuk || "" };
const U_INVOICE_LAMA = { kunci: "invoice", label: "Invoice/DO", format: "kosong",
  judul: "Tersedia setelah IMEI masuk Claim Batch", isi: () => null };
const U_MODAL = { kunci: "modal", label: "Modal Asli", format: "uang", tid: "modal",
  isi: (k) => k.harga_perolehan, urut: (k) => k.harga_perolehan || 0 };
const U_POTENSI = { kunci: "nominal", label: "Potensi Cashback", format: "nominal", tid: "cashback",
  isi: (k) => k.cashback_nominal, urut: (k) => k.cashback_nominal || 0 };

export const KOLOM_UNIT = Object.freeze({
  "aktivasi-dealer": [U_IMEI, U_SN, U_PRODUK, U_SUPPLIER, U_DO_MASUK, U_TGL_TERJUAL, U_PELANGGAN_B2B,
    U_TGL_AKTIVASI, U_NOMINAL_SUPPORT, U_STATUS, U_ALASAN],
  "aktivasi-zoa": [U_IMEI, U_SN, U_PRODUK, U_SUPPLIER, U_DO_MASUK, U_TGL_TERJUAL, U_CABANG,
    U_TGL_AKTIVASI, U_NOMINAL_SUPPORT, U_STATUS, U_ALASAN],
  "cashback-zoa": [U_IMEI, U_SN, U_UNIT_PRODUK, U_SUPPLIER, U_DO_MASUK, U_TGL_TERJUAL, U_STP, U_HARGA_BARU,
    U_NOMINAL_CB, U_TGL_AKTIVASI, U_STATUS, U_ALASAN],
  "cashback-dealer": [U_IMEI, U_SN, U_UNIT_PRODUK, U_SUPPLIER, U_DO_MASUK, U_TGL_TERJUAL, U_PELANGGAN_B2B,
    U_STP, U_HARGA_BARU, U_NOMINAL_CB, U_TGL_AKTIVASI, U_STATUS, U_ALASAN],
  lama: [U_IMEI, U_PRODUK, U_SUPPLIER, U_TGL_BELI, U_INVOICE_LAMA, U_MODAL, U_HARGA_BARU, U_POTENSI,
    U_TGL_AKTIVASI, U_STATUS, U_ALASAN],
});

// Halaman yang tabel unitnya dibagi "Terjual" / "Belum Terjual" (brief hal. 7,
// pertanyaan 20). Dealer Partner tidak punya stok gudang.
export const HALAMAN_BERBAGIAN = Object.freeze(["cashback-zoa"]);

// ============ Kolom tabel SKU di Info Program (per jenis) ============
// Nominal per SKU program jenis baru datang dari backend (`nominal_sku`, rumus
// yang sama dengan mesin). Program lama menampilkan selisih harga apa adanya —
// hak aslinya bergantung modal tiap unit, jadi tidak ada satu angka per SKU.
const S_SKU = { kunci: "sku", label: "SKU", format: "mono", isi: (it) => it.sku };
const S_PRODUK = { kunci: "produk", label: "Produk", format: "teks", isi: (it) => it.nama_produk };
export const KOLOM_SKU = Object.freeze({
  aktivasi: [S_SKU, S_PRODUK,
    { kunci: "nominal", label: "Nominal Support Aktivasi", format: "nominal", isi: (it) => it.nominal_sku }],
  cashback: [S_SKU, S_PRODUK,
    { kunci: "stp", label: "STP PROMO (Harga Lama)", format: "uang", isi: (it) => it.harga_lama },
    { kunci: "harga_baru", label: "Harga Baru", format: "uang", isi: (it) => it.harga_baru },
    { kunci: "nominal", label: "Nominal Cashback", format: "nominal", isi: (it) => it.nominal_sku }],
  lama: [S_SKU, S_PRODUK,
    { kunci: "harga_lama", label: "Harga Lama", format: "uang", isi: (it) => it.harga_lama },
    { kunci: "harga_baru", label: "Harga Baru", format: "uang", isi: (it) => it.harga_baru },
    { kunci: "selisih", label: "Selisih", format: "nominal", isi: (it) => it.harga_lama - it.harga_baru }],
});

// ============ Kolom tabel IMEI di detail Claim Batch (per jenis) ============
// "Modal (DIHAPUS)" untuk program jenis baru (brief hal. 4 & 7) — item batch
// program jenis baru memang tidak membawa modal (`harga_perolehan: null`).
const I_IMEI = { kunci: "imei", label: "IMEI", format: "imei", tid: "imei", isi: (it) => it.imei, urut: (it) => it.imei || "" };
const I_SN = { kunci: "sn", label: "SN", format: "mono", tid: "sn", isi: (it) => it.serial_number, urut: (it) => it.serial_number || "" };
const I_PRODUK = { kunci: "produk", label: "Produk", format: "produk", tid: "produk",
  isi: (it) => ({ nama: it.nama_produk, sku: it.sku }), urut: (it) => it.nama_produk || "" };
const I_TGL_AKTIVASI = { kunci: "tgl_aktivasi", label: "Tgl Aktivasi", format: "tanggal", tid: "aktivasi",
  isi: (it) => it.tanggal_aktivasi, urut: (it) => it.tanggal_aktivasi || "" };
const I_DISETUJUI = { kunci: "disetujui", label: "Disetujui", format: "uang_opsional", tid: "disetujui",
  isi: (it) => it.approved_nominal, urut: (it) => it.approved_nominal ?? -1 };
const I_STATUS = { kunci: "status", label: "Status", format: "status_item", isi: (it) => it.status };
const I_CATATAN = { kunci: "catatan", label: "Catatan", format: "catatan_item", isi: (it) => it };
export const KOLOM_ITEM = Object.freeze({
  aktivasi: [I_IMEI, I_SN, I_PRODUK, I_TGL_AKTIVASI,
    { kunci: "klaim", label: "Nominal Support Aktivasi", format: "uang", tid: "klaim",
      isi: (it) => it.cashback_nominal, urut: (it) => it.cashback_nominal || 0 },
    I_DISETUJUI, I_STATUS, I_CATATAN],
  cashback: [I_IMEI, I_SN, I_PRODUK, I_TGL_AKTIVASI,
    { kunci: "stp", label: "STP PROMO", format: "uang", tid: "stp", isi: (it) => it.harga_lama, urut: (it) => it.harga_lama || 0 },
    { kunci: "harga_baru", label: "Harga Baru", format: "uang", tid: "hargabaru",
      isi: (it) => it.harga_baru, urut: (it) => it.harga_baru || 0 },
    { kunci: "klaim", label: "Nominal Cashback", format: "uang", tid: "klaim",
      isi: (it) => it.cashback_nominal, urut: (it) => it.cashback_nominal || 0 },
    I_DISETUJUI, I_STATUS, I_CATATAN],
  lama: [I_IMEI, I_PRODUK,
    { kunci: "modal", label: "Modal Asli", format: "uang", tid: "modal",
      isi: (it) => it.harga_perolehan, urut: (it) => it.harga_perolehan || 0 },
    { kunci: "klaim", label: "Cashback Klaim", format: "uang", tid: "klaim",
      isi: (it) => it.cashback_nominal, urut: (it) => it.cashback_nominal || 0 },
    I_DISETUJUI, I_STATUS, I_CATATAN],
});

// Peta `urut` untuk `sortRows` dari daftar kolom.
export function aksesUrut(kolom) {
  const out = {};
  for (const c of kolom) if (c.urut) out[c.kunci] = c.urut;
  return out;
}

// ============ Form program ============
// Varian form (brief hal. 3): Support Aktivasi TIDAK punya Tanggal Price Drop
// dan Harga Baru; "Harga Lama" diganti "Nominal Support Aktivasi"; Periode
// Klaim diisi otomatis dari kejadian. Support Cashback & program lama: form lama.
export function itemKosong(jenis) {
  return jenis === JENIS.AKTIVASI ? { sku: "", nominal_support: "" } : { sku: "", harga_lama: "", harga_baru: "" };
}

export function formProgramValid(f, jenis) {
  const itemsOk = f.items.length > 0 && f.items.every((it) => {
    if (!String(it.sku || "").trim()) return false;
    if (jenis === JENIS.AKTIVASI) return Number(it.nominal_support) > 0;
    return Number(it.harga_lama) > 0 && Number(it.harga_baru) > 0 && Number(it.harga_baru) < Number(it.harga_lama);
  });
  const dasar = String(f.nama_program || "").trim() && String(f.supplier_nama || "").trim()
    && f.periode_aktivasi_sampai && f.deadline_submit;
  if (jenis === JENIS.AKTIVASI) return Boolean(dasar && f.periode_aktivasi_dari && itemsOk);
  return Boolean(dasar && f.tanggal_price_drop && itemsOk);
}

// Body POST/PUT program. Fungsi MURNI supaya tes node menjalankannya untuk
// ketiga varian dan tes backend mem-POST hasilnya lewat ASGI (keberatan tes-01):
// form lama selalu mengirim tanggal price drop, harga 0, dan periode klaim ""
// — kalau varian Aktivasi ikut mengirimnya, setiap program Aktivasi dari layar
// ditolak backend dengan pesan yang terdengar masuk akal.
export function bangunBodyProgram(f, jenis, kanal) {
  const dasar = {
    nama_program: String(f.nama_program || "").trim(),
    supplier_nama: String(f.supplier_nama || "").trim(),
    brand: String(f.brand || "").trim() || null,
    cabang: f.cabang || null,
    deadline_submit: f.deadline_submit,
    metode_settlement_default: f.metode_settlement_default || null,
    keterangan: f.keterangan || "",
  };
  if (jenis === JENIS.AKTIVASI) {
    return {
      ...dasar, jenis_program: jenis, kanal,
      items: f.items.map((it) => ({ sku: String(it.sku || "").trim().toUpperCase(),
                                    nominal_support: Number(it.nominal_support) })),
      periode_aktivasi_dari: f.periode_aktivasi_dari,
      periode_aktivasi_sampai: f.periode_aktivasi_sampai,
    };
  }
  const body = {
    ...dasar,
    items: f.items.map((it) => ({ sku: String(it.sku || "").trim().toUpperCase(),
                                  harga_lama: Number(it.harga_lama), harga_baru: Number(it.harga_baru) })),
    tanggal_price_drop: f.tanggal_price_drop,
    periode_aktivasi_dari: f.periode_aktivasi_dari || undefined,
    periode_aktivasi_sampai: f.periode_aktivasi_sampai,
    // STRING KOSONG, bukan null, saat periode klaim dikosongkan: backend membaca
    // null sebagai "tidak disertakan" (pola `if body.x is not None`), jadi null
    // membuat periode yang sudah terisi mustahil dihapus lewat form edit.
    periode_klaim_dari: f.periode_klaim_dari || "",
    periode_klaim_sampai: f.periode_klaim_sampai || "",
  };
  if (jenis) {
    body.jenis_program = jenis;
    body.kanal = kanal;
  }
  return body;
}
